import { useState } from "react";
import { useBlocker, useParams } from "@tanstack/react-router";
import { ReactFlowProvider } from "@xyflow/react";
import { useConfirmDialog } from "@/components/ui";
import { useCatalogoNotificaciones, useFlujo, useGuardarBorrador, usePublicarFlujo } from "../../hooks/use-notificaciones";
import { useEstadoDisenador } from "../../hooks/use-estado-disenador";
import { aBorrador } from "../../utils/lienzo";
import { BarraDisenador } from "./BarraDisenador";
import { LienzoFlujo } from "./LienzoFlujo";
import { PaletaLateral } from "./PaletaLateral";
import { PanelPropiedades } from "./panel/PanelPropiedades";

/**
 * Diseñador de un flujo (`/admin/notificaciones/$id`): paleta de eventos y canales a la
 * izquierda, lienzo React Flow al centro y propiedades del elemento seleccionado a la derecha.
 * Se edita una copia local del borrador; "Guardar" la persiste y "Publicar" la vuelve vigente.
 */
export function DisenadorFlujoPage() {
  const { id } = useParams({ from: "/_authed-tw/admin/notificaciones/$id" });
  const { data: detalle } = useFlujo(id);
  const { data: catalogo } = useCatalogoNotificaciones();
  const d = useEstadoDisenador(detalle);
  const guardar = useGuardarBorrador(id);
  const publicar = usePublicarFlujo(id);
  const confirmar = useConfirmDialog();
  const [erroresPublicacion, setErroresPublicacion] = useState<string[] | null>(null);

  useBlocker({
    shouldBlockFn: async () =>
      d.sucio &&
      !(await confirmar({
        title: "Hay cambios sin guardar",
        message: "Si sales ahora se pierden los cambios del lienzo.",
        confirmLabel: "Salir sin guardar",
        danger: true,
      })),
    enableBeforeUnload: () => d.sucio,
  });

  const guardarBorrador = async () => {
    await guardar.mutateAsync(aBorrador(d.estado));
  };

  const publicarVersion = async () => {
    setErroresPublicacion(null);
    try {
      if (d.sucio) await guardarBorrador();
      await publicar.mutateAsync();
    } catch (err) {
      const errores = (err as { response?: { data?: { errores?: string[] } } }).response?.data?.errores;
      if (errores && errores.length > 0) setErroresPublicacion(errores);
    }
  };

  return (
    <div className="flex h-[calc(100dvh-var(--shell-header-h)-2*var(--shell-padding))] flex-col overflow-hidden rounded-lg border border-border bg-card">
      <BarraDisenador
        detalle={detalle}
        sucio={d.sucio}
        guardando={guardar.isPending}
        publicando={publicar.isPending}
        erroresPublicacion={erroresPublicacion}
        onCerrarErrores={() => setErroresPublicacion(null)}
        onGuardar={() => void guardarBorrador().catch(() => undefined)}
        onPublicar={() => void publicarVersion()}
        onDescartar={d.descartar}
      />
      <ReactFlowProvider>
        <div className="grid min-h-0 flex-1 grid-cols-[260px_1fr_380px]">
          <PaletaLateral
            catalogo={catalogo}
            onAgregarEvento={(codigo) => d.agregarEvento(codigo)}
            onAgregarCanal={(canal) => d.agregarNotificacion(canal)}
          />
          <LienzoFlujo disenador={d} catalogo={catalogo.eventos} />
          <PanelPropiedades flujoId={id} disenador={d} catalogo={catalogo} erroresDelBorrador={detalle.erroresDelBorrador} />
        </div>
      </ReactFlowProvider>
    </div>
  );
}
